import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Package,
  Users,
  Building2,
  Truck,
  CreditCard,
  Receipt,
  BookOpen,
  BarChart3,
  Settings,
  LogOut,
  ChevronRight,
  DollarSign,
  UserCog,
  FileOutput,
  Cog,
  Menu,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
  { path: '/consignments', icon: Package, label: 'Consignments', module: 'consignments' },
  { path: '/customers', icon: Users, label: 'Customers', module: 'customers' },
  { path: '/core-loaders', icon: Building2, label: 'Core Loaders', module: 'core_loaders' },
  { path: '/whole-sellers', icon: Truck, label: 'Whole Sellers', module: 'whole_sellers' },
  { path: '/rates', icon: DollarSign, label: 'Rates', module: 'rates' },
  { path: '/invoices', icon: FileOutput, label: 'Invoices', module: 'invoices' },
  { path: '/payments', icon: CreditCard, label: 'Payments', module: 'payments' },
  { path: '/expenses', icon: Receipt, label: 'Expenses & Savings', module: 'expenses' },
  { path: '/ledgers', icon: BookOpen, label: 'Ledgers', module: 'ledgers' },
  { path: '/reports', icon: BarChart3, label: 'Reports', module: 'reports' },
  { path: '/staff', icon: UserCog, label: 'Staff', module: 'staff' },
  { path: '/users', icon: Settings, label: 'Users', module: 'users' },
  { path: '/settings', icon: Cog, label: 'Settings', module: 'settings' },
];

export const Sidebar = () => {
  const { user, logout, canViewModule } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // dashboard is always visible
  const filteredNavItems = navItems.filter((item) =>
    item.module === 'dashboard' ? true : canViewModule(item.module)
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="sidebar-open-btn"
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 bg-slate-900 text-white rounded-lg flex items-center justify-center shadow-md"
      >
        <Menu className="w-5 h-5" />
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
          data-testid="sidebar-backdrop"
        />
      )}

      <aside className={cn('sidebar flex flex-col', open && 'open')} data-testid="sidebar">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold font-['Chivo'] text-slate-900 text-lg">Velocity</h1>
              <p className="text-xs text-slate-500">Courier Company</p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid="sidebar-close-btn"
            className="lg:hidden w-8 h-8 flex items-center justify-center text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                className={cn('nav-item group', isActive && 'active')}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1 text-sm font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4" />}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-orange-600">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="nav-item w-full text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export const Header = ({ title, children }) => (
  <header className="header px-8 py-4" data-testid="header">
    <div className="flex items-center justify-between">
      <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900">{title}</h1>
      <div className="flex items-center gap-3">{children}</div>
    </div>
  </header>
);
